import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import api from './api';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';
import JobHistory from './JobHistory';

const ClientDashboard = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [cancel, setCancel] = useState(null); // { id, title } of job being cancelled
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/my/jobs').then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/auth?next=/dashboard/client'); return; }
    if (user.role !== 'client') { navigate('/dashboard/operative'); return; }
    load();
  }, [loading, user, navigate]);

  const openCancel = (e, job) => { e.preventDefault(); e.stopPropagation(); setReason(''); setCancel(job); };
  const confirmCancel = async () => {
    if (!cancel) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${cancel.id}`, { params: { reason: reason.trim() } });
      toast({ title: 'Job cancelled', description: 'Your job has been removed.' });
      setCancel(null); setReason(''); load();
    } catch (err) {
      toast({ title: 'Could not cancel', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#e9f3ec] py-12 min-h-[70vh]"><div className="mx-auto max-w-5xl px-5">
      <div className="flex items-center justify-between"><h1 className="ea-heading text-2xl font-bold">My Dashboard</h1><Link to="/post-a-job" className="rounded-full bg-[#3a9b5c] px-5 py-2.5 text-sm font-semibold text-white">Post a Job</Link></div>
      <h2 className="mt-8 font-display text-lg font-bold text-[#1b4f72]">My Posted Jobs</h2>
      {jobs.length === 0 ? <p className="mt-3 rounded-2xl bg-white p-6 text-sm text-gray-500 shadow">No jobs yet. Post your first job to start receiving bids.</p> : (
        <div className="mt-4 space-y-3">{jobs.map((j) => (
          <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`dash-job-${j.id}`} className="block rounded-2xl bg-white p-5 shadow-sm hover:shadow-md"><div className="flex items-center justify-between gap-3"><div><p className="font-semibold text-[#1b4f72]">{j.title}</p><p className="text-xs text-gray-400">{j.category} · {j.ref} · {j.status}</p></div><div className="flex items-center gap-3">{j.canCancel && <button data-testid={`cancel-job-${j.id}`} onClick={(e) => openCancel(e, j)} className="rounded-full border border-red-300 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50">Cancel</button>}<span className="rounded-full bg-[#e9f3ec] px-3 py-1 text-sm font-semibold text-[#3a9b5c]">{j.bidCount} bids</span></div></div></Link>
        ))}</div>
      )}
      <JobHistory role="client" />

      {cancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cancel-modal" onClick={() => !busy && setCancel(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-bold text-[#1b4f72]">Cancel this job?</h3>
              <button data-testid="cancel-modal-close" onClick={() => setCancel(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <p className="mt-1 text-sm text-gray-600">"{cancel.title}" has no bids or payment, so it will be permanently removed.</p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-gray-400">Why are you cancelling? <span className="font-normal normal-case text-gray-400">(optional — helps us improve)</span></label>
            <textarea data-testid="cancel-reason-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Found a cheaper quote, no longer needed, posted by mistake…" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#3a9b5c] focus:outline-none" />
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="cancel-modal-keep" onClick={() => setCancel(null)} disabled={busy} className="rounded-full border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">Keep Job</button>
              <button data-testid="cancel-modal-confirm" onClick={confirmCancel} disabled={busy} className="rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">{busy ? 'Cancelling…' : 'Delete Job'}</button>
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
};
export default ClientDashboard;
